import { useState, useEffect, useImperativeHandle, forwardRef } from "react";
import { Input, InputChangeEvent } from "@progress/kendo-react-inputs";
import { FiSearch } from "react-icons/fi";
import { IoCloseOutline } from "react-icons/io5";

export type UserSearchFormState = {
  username: string;
  firstName: string;
  lastName: string;
  email: string;
};

type UserWizardSearchFormProps = {
  disabled: boolean;
  onSearch: (filters: UserSearchFormState) => void;
  onCancel: () => void;
  searchError?: string;
  onClearSearchError?: () => void;
  searchCriteria?: UserSearchFormState | null;
};

export type UserSearchFormRef = {
  handleSearch: () => void;
  handleCancel: () => void;
};

export const UserWizardSearchForm = forwardRef<
  UserSearchFormRef,
  UserWizardSearchFormProps
>(function UserWizardSearchForm(
  {
    disabled,
    onSearch,
    onCancel,
    searchError,
    onClearSearchError,
    searchCriteria,
  },
  ref
) {
  const [formState, setFormState] = useState<UserSearchFormState>({
    username: searchCriteria?.username || "",
    firstName: searchCriteria?.firstName || "",
    lastName: searchCriteria?.lastName || "",
    email: searchCriteria?.email || "",
  });
  const [validationError, setValidationError] = useState<string>("");

  const option1Active = formState.username.trim().length > 0;
  const option2Active =
    formState.firstName.trim().length > 0 ||
    formState.lastName.trim().length > 0 ||
    formState.email.trim().length > 0;

  useEffect(() => {
    if (searchCriteria) {
      setFormState({
        username: searchCriteria.username || "",
        firstName: searchCriteria.firstName || "",
        lastName: searchCriteria.lastName || "",
        email: searchCriteria.email || "",
      });
    }
  }, [searchCriteria]);

  const handleChange = (key: keyof UserSearchFormState, value: string) => {
    setFormState((prev) => ({ ...prev, [key]: value }));
    if (validationError) setValidationError("");
    if (searchError && onClearSearchError) onClearSearchError();
  };

  const handleClearOption1 = () => {
    setFormState((prev) => ({ ...prev, username: "" }));
    if (validationError) setValidationError("");
    if (searchError && onClearSearchError) onClearSearchError();
  };

  const handleClearOption2 = () => {
    setFormState((prev) => ({
      ...prev,
      firstName: "",
      lastName: "",
      email: "",
    }));
    if (validationError) setValidationError("");
    if (searchError && onClearSearchError) onClearSearchError();
  };

  const validateForm = (): boolean => {
    if (formState.username.trim().length > 0) return true;

    const hasAnyOption2 =
      formState.firstName.trim().length > 0 ||
      formState.lastName.trim().length > 0 ||
      formState.email.trim().length > 0;

    if (hasAnyOption2) return true;

    setValidationError(
      "Please enter a Username or at least one of First Name, Last Name, or Email."
    );
    return false;
  };

  const handleSearchClick = () => {
    if (!disabled && validateForm()) {
      onSearch(formState);
    }
  };

  const handleCancelClick = () => {
    onCancel();
  };

  useImperativeHandle(ref, () => ({
    handleSearch: handleSearchClick,
    handleCancel: handleCancelClick,
  }));

  return (
    <div style={{ padding: "0 24px 24px", background: "white" }}>
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          marginBottom: "24px",
          gap: "12px",
        }}
      >
        <div
          style={{
            width: "40px",
            height: "40px",
            background: "var(--color-p3, #1e5a78)",
            borderRadius: "4px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <FiSearch size={18} color="white" />
        </div>
        <p
          style={{
            margin: 0,
            fontSize: "14px",
            color: "#666",
            lineHeight: "1.5",
          }}
        >
          Search for an existing user account before creating a new one.
        </p>
      </div>

      <div
        style={{
          marginBottom: "24px",
          padding: "16px",
          background: option1Active ? "#f8f9fa" : "transparent",
          borderLeft: option1Active
            ? "4px solid var(--color-p3, #1e5a78)"
            : "4px solid transparent",
          borderRadius: "4px",
          opacity: option2Active ? 0.3 : 1,
          pointerEvents: option2Active ? "none" : "auto",
          transition: "opacity 0.2s, border-color 0.2s",
        }}
        aria-disabled={option2Active}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "8px",
          }}
        >
          <label style={{ fontSize: "13px", color: "#666", fontWeight: "500" }}>
            Option 1
          </label>
          {option1Active && (
            <button
              type="button"
              onClick={handleClearOption1}
              disabled={disabled}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "4px",
                background: "none",
                border: "none",
                color: "#666",
                fontSize: "13px",
                cursor: "pointer",
                padding: "4px",
              }}
            >
              <IoCloseOutline size={16} />
              Clear
            </button>
          )}
        </div>

        <label
          style={{
            display: "block",
            marginBottom: "8px",
            fontSize: "14px",
            color: "#333",
          }}
        >
          Search by Username
        </label>
        <Input
          style={{
            width: "100%",
            maxWidth: "340px",
            border: option1Active
              ? "2px solid var(--color-p3, #1e5a78)"
              : undefined,
          }}
          value={formState.username}
          onChange={(e: InputChangeEvent) =>
            handleChange("username", e.value || "")
          }
          placeholder="Enter username"
          disabled={disabled || option2Active}
        />

        {searchError && option1Active && !option2Active && (
          <div
            style={{
              padding: "12px 16px",
              background: "#f8d7da",
              border: "1px solid #f5c2c7",
              borderRadius: "4px",
              marginTop: "12px",
              fontSize: "14px",
              color: "#842029",
            }}
          >
            {searchError}
          </div>
        )}
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          margin: "32px 0",
          gap: "16px",
        }}
      >
        <div style={{ flex: 1, height: "1px", background: "#ccc" }} />
        <span style={{ color: "#999", fontSize: "14px", fontWeight: 500 }}>
          OR
        </span>
        <div style={{ flex: 1, height: "1px", background: "#ccc" }} />
      </div>

      {/* Option 2: Name / Email */}
      <div
        style={{
          marginBottom: "24px",
          padding: "16px",
          background: option2Active ? "#f8f9fa" : "transparent",
          borderLeft: option2Active
            ? "4px solid var(--color-p3, #1e5a78)"
            : "4px solid transparent",
          borderRadius: "4px",
          opacity: option1Active ? 0.3 : 1,
          pointerEvents: option1Active ? "none" : "auto",
          transition: "opacity 0.2s, border-color 0.2s",
        }}
        aria-disabled={option1Active}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "8px",
          }}
        >
          <label style={{ fontSize: "13px", color: "#666", fontWeight: "500" }}>
            Option 2
          </label>
          {option2Active && (
            <button
              type="button"
              onClick={handleClearOption2}
              disabled={disabled}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "4px",
                background: "none",
                border: "none",
                color: "#666",
                fontSize: "13px",
                cursor: "pointer",
                padding: "4px",
              }}
            >
              <IoCloseOutline size={16} />
              Clear
            </button>
          )}
        </div>

        <label
          style={{
            display: "block",
            marginBottom: "16px",
            fontSize: "14px",
            color: "#333",
          }}
        >
          Search by First Name, Last Name, and/or Email
        </label>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: "16px",
          }}
        >
          <div>
            <label
              style={{
                display: "block",
                marginBottom: "6px",
                fontSize: "13px",
                color: "#666",
              }}
            >
              First Name
            </label>
            <Input
              style={{ width: "100%" }}
              value={formState.firstName}
              onChange={(e: InputChangeEvent) =>
                handleChange("firstName", e.value || "")
              }
              placeholder="Enter first name"
              disabled={disabled || option1Active}
            />
          </div>

          <div>
            <label
              style={{
                display: "block",
                marginBottom: "6px",
                fontSize: "13px",
                color: "#666",
              }}
            >
              Last Name
            </label>
            <Input
              style={{ width: "100%" }}
              value={formState.lastName}
              onChange={(e: InputChangeEvent) =>
                handleChange("lastName", e.value || "")
              }
              placeholder="Enter last name"
              disabled={disabled || option1Active}
            />
          </div>

          <div>
            <label
              style={{
                display: "block",
                marginBottom: "6px",
                fontSize: "13px",
                color: "#666",
              }}
            >
              Email
            </label>
            <Input
              style={{ width: "100%" }}
              value={formState.email}
              onChange={(e: InputChangeEvent) =>
                handleChange("email", e.value || "")
              }
              placeholder="Enter email"
              disabled={disabled || option1Active}
            />
          </div>
        </div>

        {searchError && option2Active && !option1Active && (
          <div
            style={{
              padding: "12px 16px",
              background: "#f8d7da",
              border: "1px solid #f5c2c7",
              borderRadius: "4px",
              marginTop: "12px",
              fontSize: "14px",
              color: "#842029",
            }}
          >
            {searchError}
          </div>
        )}
      </div>

      {validationError && (
        <div
          style={{
            padding: "12px 16px",
            background: "#fff3cd",
            border: "1px solid #ffc107",
            borderRadius: "4px",
            marginBottom: "16px",
            fontSize: "14px",
            color: "#856404",
          }}
        >
          {validationError}
        </div>
      )}
    </div>
  );
});
